<template>
    <div :class="['ui-tabs', `ui-tabs--${variant}`, `ui-tabs--${theme}`]">
        <TabsNavigation
            v-model="activeTab"
            :items="tabs"
            :centered="centered"
            :vertical="vertical"
        >
            <div
                v-for="(item, index) in tabs"
                :key="item.slotName + index"
            >
                <slot
                    v-if="activeTab === index"
                    :name="item.slotName"
                />
            </div>
        </TabsNavigation>
    </div>
</template>

<script>
import {defineComponent, ref, watch} from 'vue';
import {useRoute} from '@nuxtjs/composition-api';

import {TABS_THEMES, TABS_VARIANTS} from '@/core/constants/components/tabsProps';
import {CURRENT_TAB_PARAM} from '@/core/models/pages/for-individuals/crediting/types/queryParamsTypes';
import TabsNavigation from '@/components/UI-components/Tabs/TabsNavigation.vue';

export default defineComponent({
    name: 'UITabs',
    components: {
        TabsNavigation,
    },
    props: {
        tabs: {
            type: Array,
            required: true,
        },
        currentTab: {
            type: Number,
            default: () => 0,
        },
        centered: {
            type: Boolean,
            default: () => false,
        },
        vertical: {
            type: Boolean,
            default: () => false,
        },
        variant: {
            type: String,
            default: () => TABS_VARIANTS.UNDERLINE,
            validator: (value) => {
                return Object.values(TABS_VARIANTS).includes(value);
            },
        },
        theme: {
            type: String,
            default: () => TABS_THEMES.GREEN,
            validator: (value) => {
                return Object.values(TABS_THEMES).includes(value);
            },
        },
    },
    emits: [
        'change',
        'tab-change',
    ],
    setup(props, context) {
        const route = useRoute();

        const activeTab = ref(0);

        watch(() => route.value.query, () => {
            if (route.value.query?.[CURRENT_TAB_PARAM]) {
                activeTab.value = +route.value.query[CURRENT_TAB_PARAM];
            }
        }, {immediate: true});

        watch(() => props.currentTab, (newVal) => {
            if (!route.value.query?.[CURRENT_TAB_PARAM]) {
                activeTab.value = newVal;
            }
        }, {immediate: true});

        watch(activeTab, (newVal) => {
            context.emit('change', newVal);
            context.emit('tab-change', props.tabs[newVal]);
        }, {immediate: true});

        return {
            activeTab,
        };
    },
});
</script>

<style lang="scss">
@import '@/assets/styles/ui-elements/ui-tabs';
</style>
